import { action, computed, observable, runInAction } from "mobx";

import { compareDatesOrUndefinedPredicate } from "@bps/date-time";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { maybePromiseObservable } from "@libs/utils/promise-observable/promise-observable.utils.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { AccStore } from "@stores/acc/AccStore.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";

export class ConditionAppointmentFormHelper {
  @observable patientId: string | undefined;

  @observable episodeOfCareId: string | undefined;

  @observable
  conditionsPromise = maybePromiseObservable<EpisodeOfCare[]>();

  autoLinkConditionEnabled?: boolean;

  constructor(
    private root: IRootStore,
    _episodeOfCare?: EpisodeOfCare
  ) {
    if (_episodeOfCare && this.episodesOfCare.length === 0) {
      runInAction(() => {
        this.conditionsPromise.set(Promise.resolve([_episodeOfCare]));
        this.setEpisodeOfCareId(_episodeOfCare.id);
      });
    }
  }

  @action
  setPatientId = (patientId: string | undefined) => {
    this.patientId = patientId;
  };

  @action
  setEpisodeOfCareId = (episodeOfCareId: string | undefined) => {
    this.episodeOfCareId = episodeOfCareId;
  };

  private get acc(): AccStore {
    return this.root.acc;
  }

  @computed
  get episodesOfCare() {
    return this.conditionsPromise.value ? this.conditionsPromise.value : [];
  }

  @computed
  get episodeOfCare() {
    return this.episodeOfCareId
      ? this.episodesOfCare.find(x => x.id === this.episodeOfCareId)
      : undefined;
  }

  @computed
  get activeEpisodesOfCare() {
    return this.episodesOfCare.filter(x => !x.discharged);
  }

  getEpisodesOfCare = async () => {
    if (this.patientId) {
      const episodesOfCare = await this.root.clinical.getPatientEpisodesOfCare(
        this.patientId
      );

      if (!episodesOfCare.length) {
        return [];
      }

      await Promise.all([
        this.root.acc.getClaimEpisodesOfCare({
          episodesOfCare: episodesOfCare.map(x => x.id)
        }),
        this.root.core.hasPermissions(Permission.ClaimRead)
          ? this.root.acc.fetchClaims({ patients: [this.patientId] })
          : undefined
      ]);

      return episodesOfCare;
    }

    return [];
  };

  getSortedEpisodesOfCare = () => {
    return Array.from(
      this.episodesOfCare.sort((a, b) => {
        if (this.episodeOfCare?.id === a.id) {
          return -1;
        } else if (this.episodeOfCare?.id === b.id) {
          return 1;
        }
        return compareDatesOrUndefinedPredicate(a.sortDate, b.sortDate, true);
      })
    );
  };

  public async autoLinkCondition() {
    await this.loadAutoLinkConditionSetting();

    if (this.autoLinkConditionEnabled) {
      if (this.activeEpisodesOfCare.length === 1) {
        this.setEpisodeOfCareId(this.activeEpisodesOfCare[0].id);
      }
    }
  }

  private async loadAutoLinkConditionSetting() {
    if (this.autoLinkConditionEnabled === undefined) {
      const orgUnitId = this.root.core.locationId;

      if (orgUnitId) {
        const orgUnit =
          await this.root.userExperience.getOrgUnitSetting(orgUnitId);
        this.autoLinkConditionEnabled =
          orgUnit?.preferenceSetting?.autoLinkCondition ?? false;
      }
    }
  }

  public getEpisodeOfCareInitialValues() {
    return {
      episodeOfCareId: this.episodeOfCare?.id,
      patientId: this.episodeOfCare?.patientId,
      providerId: this.episodeOfCare?.providerId
    };
  }
}
